import type { PblDocument, PblMeeting } from '@/types/pbl';

const getMetadataText = (metadata: PblMeeting['metadata'], key: string) => {
  const value = metadata?.[key];

  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
};

const endedMeetingStatuses = new Set(['completed', 'ended', 'recorded', 'recording_ready']);
const cancelledMeetingStatuses = new Set(['cancelled', 'canceled']);

const getDateTime = (value?: string | null) => {
  const normalizedValue = value?.trim().replace(' ', 'T');
  const time = normalizedValue ? new Date(normalizedValue).getTime() : Number.NaN;

  return Number.isFinite(time) ? time : undefined;
};

export const formatDateTime = (value?: string | null) => {
  if (!value) return '-';

  const normalizedValue = value.trim();
  const isoDateTimeMatch = normalizedValue.match(/^(\d{4}[-/]\d{2}[-/]\d{2})[T ](\d{2}:\d{2})(?::\d{2})?/);

  if (isoDateTimeMatch) {
    return `${isoDateTimeMatch[1].replace(/-/g, '/')} ${isoDateTimeMatch[2]}`;
  }

  const [date, time] = normalizedValue.split('T');
  const normalizedDate = date ? date.replace(/-/g, '/') : value;
  const normalizedTime = time ? time.slice(0, 5) : '';

  return normalizedTime ? `${normalizedDate} ${normalizedTime}` : normalizedDate;
};

export const formatMeetingDateRange = (startAt?: string | null, endAt?: string | null) => {
  const startDate = formatDateTime(startAt);
  if (!endAt) return startDate;

  const endDate = formatDateTime(endAt);
  const [startDay, startTime] = startDate.split(' ');
  const [endDay, endTime] = endDate.split(' ');

  return startDay === endDay && startTime && endTime
    ? `${startDay}\n${startTime}\u2013${endTime}`
    : `${startDate}\n${endDate}`;
};

export const formatMeetingDate = (meeting: PblMeeting) =>
  formatMeetingDateRange(
    meeting.scheduled_at_display || meeting.scheduled_at,
    meeting.ends_at_display ||
      meeting.ends_at ||
      getMetadataText(meeting.metadata, 'end_at_display') ||
      getMetadataText(meeting.metadata, 'end_at')
  );

export const canEditMeetingTime = (meeting: PblMeeting) =>
  String(meeting.status || '').trim().toLowerCase() === 'scheduled';

export const isMeetingEnded = (meeting: PblMeeting, now = Date.now()) => {
  const status = String(meeting.status || '').trim().toLowerCase();
  if (endedMeetingStatuses.has(status)) return true;

  const endTime = getDateTime(
    meeting.ends_at ||
      getMetadataText(meeting.metadata, 'end_at') ||
      getMetadataText(meeting.metadata, 'ended_at') ||
      getMetadataText(meeting.metadata, 'endedAt')
  );

  return typeof endTime === 'number' && endTime <= now;
};

export const isMeetingCancelled = (meeting: PblMeeting) => {
  const status = String(meeting.status || '').trim().toLowerCase();

  return cancelledMeetingStatuses.has(status);
};

export const isMeetingNew = (meeting: PblMeeting, now = Date.now()) => {
  const createdAt = getDateTime(meeting.created_at);

  return typeof createdAt === 'number' && now - createdAt >= 0 && now - createdAt <= 24 * 60 * 60 * 1000;
};

export const getMeetingBadgeKey = (meeting: PblMeeting, showEndedBadge: boolean, now = Date.now()) => {
  if (isMeetingCancelled(meeting)) return 'cancelled';
  if (showEndedBadge && isMeetingEnded(meeting, now)) return 'ended';
  if (isMeetingNew(meeting, now)) return 'new';

  return null;
};

export const formatFileSize = (size?: number | string | null) => {
  let normalizedSize = size;

  if (typeof normalizedSize === 'string') {
    const trimmed = normalizedSize.trim();

    if (!trimmed) return '-';
    if (/[a-zA-Z]/.test(trimmed)) {
      if (/\b(?:B|KB|MB|GB|TB)\b/i.test(trimmed)) return trimmed;

      return '-';
    }

    const parsed = Number(trimmed);

    normalizedSize = Number.isFinite(parsed) ? parsed : undefined;
  }

  if (typeof normalizedSize !== 'number' || !Number.isFinite(normalizedSize)) return '-';
  if (normalizedSize >= 1024 * 1024) return `${(normalizedSize / 1024 / 1024).toFixed(1)} MB`;
  if (normalizedSize >= 1024) return `${Math.ceil(normalizedSize / 1024)} KB`;

  return `${normalizedSize} B`;
};

export const getMeetingName = (meeting: PblMeeting) =>
  meeting.display_title ||
  `${meeting.purpose === 'oc_intake' ? 'OC Intake Meeting' : 'Lawyer Consultation'}${
    meeting.sequence_number ? ` #${meeting.sequence_number}` : ''
  }`;

export const getDocumentFileName = (document: PblDocument) =>
  document.storage_file?.name || document.title || `Document #${document.id}`;

export const getDocumentFilePath = (document: PblDocument) =>
  document.signed_storage_file?.path || document.storage_file?.path;

export const getDocumentFileType = (document: PblDocument) =>
  document.storage_file?.extension || document.document_type || '-';

export const isSupportingDocument = (document: PblDocument) => document.document_type === 'supporting_document';

export const getDocumentMetadata = (document: PblDocument) => {
  const metadata = (document as PblDocument & { metadata?: Record<string, unknown> | null }).metadata;

  return metadata && typeof metadata === 'object' ? metadata : {};
};

export const getDocumentMetadataText = (document: PblDocument, keys: string[]) => {
  const metadata = getDocumentMetadata(document);

  for (const key of keys) {
    const value = metadata[key];

    if (typeof value === 'string' && value.trim()) return value.trim();
  }

  return undefined;
};

export const getDocumentUploaderName = (document: PblDocument) => {
  const name = document.uploaded_by?.name?.trim();

  return name || getDocumentMetadataText(document, ['uploaded_by_name', 'uploaded_by']);
};
